import type { SvgIconComponent } from '@mui/icons-material';
import DirectionsBike from '@mui/icons-material/DirectionsBike';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import Computer from '@mui/icons-material/Computer';
import { Type } from '../types/Event/Type';
import { Pace } from '../types/Event/Pace';
import { Distance } from '../types/Event/Distance';
import { Terrain } from '../types/Event/Terrain';
import { Regroup } from '../types/Event/Regroup';
import { WeatherCancellation } from '../types/Event/WeatherCancellation';
import { Visibility } from '../types/Event/Visibility';
import { AgeRestriction } from '../types/Event/AgeRestriction';
import { RegistrationType } from '../types/Event/RegistrationType';

// IMPORTANT: update server utils when changing
export const eventTypes: Record<Type, { name: string; icon: SvgIconComponent }> = {
  [Type.Ride]: {
    name: 'Ride',
    icon: DirectionsBike,
  },
  [Type.Event]: {
    name: 'Event',
    icon: CalendarMonth,
  },
  [Type.OnlineEvent]: {
    name: 'Online Event',
    icon: Computer,
  },
};

// IMPORTANT: update server utils when changing
export const eventPaces: Record<Pace, { name: string, description: string }> = {
  [Pace.Easy]: {
    name: 'Easy',
    description: 'Under 10 mph',
  },
  [Pace.Leisurely]: {
    name: 'Leisurely',
    description: '10-12 mph',
  },
  [Pace.Steady]: {
    name: 'Steady',
    description: '12-14 mph',
  },
  [Pace.Moderate]: {
    name: 'Moderate',
    description: '14-16 mph',
  },
  [Pace.Brisk]: {
    name: 'Brisk',
    description: '16-18 mph',
  },
  [Pace.Vigorous]: {
    name: 'Vigorous',
    description: '18-20 mph',
  },
  [Pace.Strenuous]: {
    name: 'Strenuous',
    description: '20-22 mph',
  },
  [Pace.SuperStrenuous]: {
    name: 'Super Strenuous',
    description: '22+ mph',
  },
};

// IMPORTANT: update server utils when changing
export const eventDistances: Record<Distance, { name: string }> = {
  [Distance.ZeroToThree]: {
    name: '0-3 miles',
  },
  [Distance.ThreeToEight]: {
    name: '3-8 miles',
  },
  [Distance.EightToFifteen]: {
    name: '8-15 miles',
  },
  [Distance.FifteenPlus]: {
    name: '15+ miles',
  },
};

// IMPORTANT: update server utils when changing
export const eventTerrains: Record<Terrain, { name: string, description: string }> = {
  [Terrain.MostlyFlat]: {
    name: 'Mostly flat',
    description: 'Trails and/or mostly flat roads with a possible gentle uphill',
  },
  [Terrain.Rolling]: {
    name: 'Rolling',
    description: 'Climbs are short and easy, not too numerous',
  },
  [Terrain.SomeHills]: {
    name: 'Some hills',
    description: 'A few short, steep hills, some moderate upgrades and/or longer gentle climbs',
  },
  [Terrain.Hilly]: {
    name: 'Hilly',
    description: 'Consistent or continuous climbing',
  },
  [Terrain.OffRoad]: {
    name: 'Off-road',
    description: 'Significant unpaved sections',
  },
};

// IMPORTANT: update server utils when changing
export const eventRegroups: Record<Regroup, { name: string }> = {
  [Regroup.None]: {
    name: 'No regroups',
  },
  [Regroup.Occasional]: {
    name: 'Occasional regroups',
  },
  [Regroup.Frequent]: {
    name: 'Frequent regroups',
  },
  [Regroup.StayTogether]: {
    name: 'Stay together',
  },
};

// IMPORTANT: update server utils when changing
export const eventWeatherCancellations: Record<WeatherCancellation, { name: string }> = {
  [WeatherCancellation.ShowersCancel]: {
    name: 'Showers cancel',
  },
  [WeatherCancellation.SteadyRainCancel]: {
    name: 'Steady rain cancels',
  },
  [WeatherCancellation.IceSnowCancel]: {
    name: 'Ice/snow cancels',
  },
  [WeatherCancellation.AllWeather]: {
    name: 'Rain or shine',
  },
};

export const visibilityLabels: Record<Visibility, string> = {
  [Visibility.Public]: 'Public',
  [Visibility.Group]: 'Group',
  [Visibility.Unlisted]: 'Unlisted',
  [Visibility.Private]: 'Private',
};

// IMPORTANT: update server utils when changing
export const prettyVisibility = (event: { visibility: Visibility }, options?: { group?: { name: string } | null }) => {
  const { visibility } = event;
  const group = options?.group;

  if (visibility === Visibility.Public) {
    return visibilityLabels[visibility];
  } else if (visibility === Visibility.Group) {
    return `🔒 Members of ${group ? group.name : 'Group'}`;
  }
  return `🔒 ${visibilityLabels[visibility]}`;
};

// IMPORTANT: update server utils when changing
export const eventAgeRestrictions: Record<AgeRestriction, { name: string, description: string }> = {
  [AgeRestriction.General]: {
    name: 'General',
    description: 'most rides and events',
  },
  [AgeRestriction.Family]: {
    name: 'Family Friendly',
    description: 'distance, content, and pace is appropriate for kids',
  },
  [AgeRestriction.TwentyOnePlus]: {
    name: 'Adults only (21+)',
    description: 'drinking or other adult activities (nudity, etc)',
  },
};

// IMPORTANT: update server utils when changing
export const eventRegistrationTypes: Record<RegistrationType, { name: string, description?: string }> = {
  [RegistrationType.Open]: {
    name: 'Open Listing',
  },
  [RegistrationType.ExternalRegistration]: {
    name: 'External Registration Required',
  },
  [RegistrationType.ApprovalRequired]: {
    name: 'Everyday Rides Request to Join',
    description: 'vet your riders — great for limited-capacity rides, private routes, or skill-gated events',
  },
};
